/* eslint-disable */
import React, { useState } from 'react';
import DeploymentHome from './DeploymentHome';
import DeploymentLocationShifts from './DeploymentLocationShifts';
import DeploymentShiftDetail from './DeploymentShiftDetail';
import ScanOutSheet from './ScanOutSheet';
import ScanInSheet from './ScanInSheet';
import SwapSheet from './SwapSheet';
import CloseShiftSheet from './CloseShiftSheet';

/**
 * DeploymentMobileApp — mobile shell for Deployment-role users.
 * Has its own screen-based routing, similar to PhoneView.
 */
export default function DeploymentMobileApp({ st, setSt, account, audit, userRole, deploymentLocationIds }) {
  const [screen, setScreen] = useState('home');
  const [selLocationId, setSelLocationId] = useState(null);
  const [selShiftId, setSelShiftId] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // For non-deployment users, show all locations
  const effectiveLocationIds = userRole === 'Deployment'
    ? deploymentLocationIds
    : (st.locations || []).map(l => l.id);

  const initials = (account?.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  // Success screen after scan out/in/swap
  if (screen === 'success' && successData) {
    const { type, device, alloc, role, operativeName, returningDevice, replacementDevice } = successData;
    const title = type === 'scan-out' ? `Allocated · ${device?.serial}` : type === 'scan-in' ? `Returned · ${device?.serial}` : `Swapped · ${returningDevice?.serial} → ${replacementDevice?.serial}`;
    const sub = type === 'scan-out' ? `${role} · ${operativeName || 'unassigned'}` : type === 'scan-in' ? `Allocation closed at ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Swap completed';

    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-app)', color: 'var(--text-default)', fontFamily: "'DM Sans','Aptos','Segoe UI',-apple-system,sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(74,222,128,0.12)', border: '2px solid #4ade80', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>{sub}</div>
        <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 320 }}>
          <button onClick={() => { setScreen('shift-detail'); setSuccessData(null); }} style={{ flex: 1, padding: 13, borderRadius: 10, border: '1px solid var(--border-default)', background: 'var(--surface-inset)', color: 'var(--text-default)', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>Back to shift</button>
          <button onClick={() => { setScreen(type === 'scan-in' ? 'scan-in' : 'scan-out'); setSuccessData(null); }} style={{ flex: 1, padding: 13, borderRadius: 10, border: 'none', background: '#ff530d', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>Scan next</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-app)', color: 'var(--text-default)', fontFamily: "'DM Sans','Aptos','Segoe UI',-apple-system,sans-serif", display: 'flex', flexDirection: 'column' }}>
      {/* Top bar — only shown on home screen */}
      {screen === 'home' && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#ff530d 0%,#e04200 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#fff' }}>O</div>
            <span style={{ fontWeight: 600, fontSize: 14 }}>OWL Bench<span style={{ marginLeft: 6, padding: '2px 6px', background: 'rgba(255,83,13,0.15)', border: '1px solid rgba(255,83,13,0.35)', color: '#ff530d', borderRadius: 4, fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', verticalAlign: 'middle' }}>Deploy</span></span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 4px', background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 999, fontSize: 12, color: 'var(--text-muted)' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--surface-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 10, color: 'var(--text-default)' }}>{initials}</div>
            <span>{account?.name || account?.username || 'User'}</span>
          </div>
        </div>
      )}

      {/* Screen content */}
      {screen === 'home' && (
        <DeploymentHome
          st={st}
          account={account}
          deploymentLocationIds={effectiveLocationIds}
          onSelectLocation={(locId) => { setSelLocationId(locId); setScreen('location-shifts'); }}
        />
      )}

      {screen === 'location-shifts' && (
        <DeploymentLocationShifts
          st={st}
          setSt={setSt}
          locationId={selLocationId}
          account={account}
          onSelectShift={(shiftId) => { setSelShiftId(shiftId); setScreen('shift-detail'); }}
          onBack={() => setScreen('home')}
          onCreateShift={() => {/* TODO: inline create shift form */}}
        />
      )}

      {screen === 'shift-detail' && (
        <DeploymentShiftDetail
          st={st}
          setSt={setSt}
          shiftId={selShiftId}
          account={account}
          audit={audit}
          onBack={() => setScreen('location-shifts')}
          onScanOut={() => setScreen('scan-out')}
          onScanIn={() => setScreen('scan-in')}
          onSwap={() => setScreen('swap')}
          onCloseShift={() => setScreen('close-shift')}
        />
      )}

      {screen === 'scan-out' && (
        <ScanOutSheet
          st={st}
          setSt={setSt}
          shiftId={selShiftId}
          account={account}
          audit={audit}
          onBack={() => setScreen('shift-detail')}
          onSuccess={(data) => { setSuccessData({ type: 'scan-out', ...data }); setScreen('success'); }}
        />
      )}

      {screen === 'scan-in' && (
        <ScanInSheet
          st={st}
          setSt={setSt}
          shiftId={selShiftId}
          account={account}
          audit={audit}
          onBack={() => setScreen('shift-detail')}
          onSuccess={(data) => { setSuccessData({ type: 'scan-in', ...data }); setScreen('success'); }}
        />
      )}

      {screen === 'swap' && (
        <SwapSheet
          st={st}
          setSt={setSt}
          shiftId={selShiftId}
          account={account}
          audit={audit}
          onBack={() => setScreen('shift-detail')}
          onSuccess={(data) => { setSuccessData({ type: 'swap', ...data }); setScreen('success'); }}
        />
      )}

      {screen === 'close-shift' && (
        <CloseShiftSheet
          st={st}
          setSt={setSt}
          shiftId={selShiftId}
          account={account}
          audit={audit}
          onBack={() => setScreen('shift-detail')}
          onSuccess={() => { setScreen('location-shifts'); }}
        />
      )}
    </div>
  );
}
